import {
  DataTable,
  ScrollArea,
  ScrollBar,
  Button,
  V2Loader,
  DataTablePagination,
  SearchInput,
} from "web-design-system";
import { useFunds } from "creso-core/hooks";
import { useMemo, useState, useRef } from "react";
import {
  ColumnPinningState,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { useNavigate } from "@tanstack/react-router";
import { FilterIcon, X } from "lucide-react";
import { EditColumnsDialog } from "@core/components/table/edit-fields-dialog";
import { FundFilterChips } from "@funds/components/filters/filter-chips";
import { isEmpty } from "radash";
import { columns, FUND_SORT_FIELDS } from "@funds/components/column";
import { useFundsTableState } from "@funds/hooks/use-funds-table-state";
import { useFundsTableConfig } from "@funds/hooks/use-funds-table-config";
import { Page } from "@core/components/layout/page";
import { FundsFilter } from "../components/filters/funds-filter";
import { SunriseFundsBanner } from "../components/sunrise-funds-banner";

export function usePrevious<T>(value: T): T | undefined {
  const currentRef = useRef<T>(value);
  const previousRef = useRef<T | undefined>(undefined);

  if (currentRef.current !== value) {
    previousRef.current = currentRef.current;
    currentRef.current = value;
  }

  return previousRef.current;
}

export default function FundsListPage() {
  const navigate = useNavigate();

  const {
    filters,
    sort,
    updateSort,
    pagination,
    updatePagination,
    search,
    updateSearch,
    updateFilters,
  } = useFundsTableState();

  // The table sorts by column id; the API needs its own ordering field
  const apiSort = useMemo(
    () => sort.map((s) => ({ ...s, id: FUND_SORT_FIELDS[s.id] ?? s.id })),
    [sort]
  );

  const { data, isPlaceholderData, isPending } = useFunds({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    search,
    filters,
    sort: apiSort,
    accumulatedDistributionStatus: true,
  });

  const funds = useMemo(() => data?.results || [], [data?.results]);

  const {
    columnOrder,
    onColumnOrderChange,
    columnVisibility,
    onColumnVisibilityChange,
  } = useFundsTableConfig();

  const columnPinning = useMemo<ColumnPinningState>(
    () => ({
      left: ["fund_name"],
      right: ["watchlist"],
    }),
    []
  );

  const table = useReactTable({
    data: funds,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    rowCount: data?.count,
    onPaginationChange: updatePagination,
    state: {
      pagination,
      columnOrder,
      columnVisibility,
      columnPinning,
      sorting: sort,
    },
    onColumnOrderChange,
    onColumnVisibilityChange,
    manualSorting: true,
    enableMultiSort: false,
    enableSortingRemoval: true,
    defaultColumn: {
      minSize: 150,
      enablePinning: true,
      maxSize: 250,
    },
    enableRowSelection: false,
    onSortingChange: updateSort,
  });

  const addNewTransaction = () => {
    navigate({
      to: ".",
      search: {
        modal: "transaction",
      },
    });
  };

  const editColumnConfig = useMemo(() => {
    return columns
      .map((c) => {
        return {
          id: c.id as string,
          label: c.label,
          visible: columnVisibility[c.id as keyof VisibilityState],
          group: c.group,
          configurable: c.configurable ?? true,
        };
      })
      .toSorted(
        (a, b) =>
          columnOrder.indexOf(a.id as string) -
          columnOrder.indexOf(b.id as string)
      );
  }, [columnVisibility, columnOrder]);

  const [showFilters, setShowFilters] = useState(false);

  const [filteredCount, totalCount] = useMemo(
    () => [data?.count, data?.total],
    [data]
  );

  return (
    <>
      <Page.Header
        title="Mutual Funds"
        actions={<Button onClick={addNewTransaction}>New transaction</Button>}
      />
      <Page.Body className="flex-row gap-2 overflow-hidden">
        <FundsFilter
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          onFilterVisibilityChange={(visibilityChanges) => {
            const validColumnIds = columns.map((c) => c.id);
            const changes = Object.fromEntries(
              Object.entries(visibilityChanges).filter(
                ([k, v]) => validColumnIds.includes(k) && v
              )
            );
            onColumnVisibilityChange((cv) => ({ ...cv, ...changes }));
          }}
        />
        <div className="flex flex-col flex-1 overflow-hidden pl-1 -ml-1 -mt-2 pt-2 gap-2">
          {Object.values(filters)?.some((v) => !isEmpty(v)) ? (
            <div className="flex flex-col gap-2 mb-1">
              {filteredCount !== totalCount && (
                <div className="flex gap-2 items-center">
                  <span className="text-xs">
                    Filtered: {filteredCount} of {totalCount}
                  </span>
                  <Button
                    size="sm"
                    className="p-0 h-auto"
                    variant="link"
                    onClick={() => {
                      updateFilters?.({});
                    }}
                  >
                    <X />
                    Clear all
                  </Button>
                </div>
              )}
              <FundFilterChips />
            </div>
          ) : null}
          <SunriseFundsBanner
            onFilter={() =>
              updateFilters((prev) => ({ ...prev, is_sunrise_fund: "true" }))
            }
          />
          <div className="flex flex-row gap-2">
            <SearchInput
              placeholder="Search"
              className="w-75"
              defaultValue={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateSearch(e.target.value)
              }
            />
            <div className="flex flex-row gap-2 ml-auto shrink-0">
              <Button
                size="icon"
                variant="outline"
                className="lg:hidden shrink-0"
                onClick={() => setShowFilters(true)}
              >
                <FilterIcon className="size-4" />
              </Button>
              <EditColumnsDialog
                columnsConfig={editColumnConfig}
                onReorder={onColumnOrderChange}
                onVisibilityChange={onColumnVisibilityChange}
                label="Edit columns"
                header="Customise table"
              />
            </div>
          </div>
          <V2Loader
            isLoading={isPending}
            className="flex flex-col flex-1 w-full overflow-hidden border border-border rounded-lg"
          >
            <ScrollArea className="flex-1 flex flex-col">
              <DataTable
                disabled={isPlaceholderData}
                table={table}
                className="border-0 flex-1"
                pagination={false}
              />
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
            <DataTablePagination
              table={table}
              className="sticky bottom-0 z-20"
            />
          </V2Loader>
        </div>
      </Page.Body>
    </>
  );
}
